/* eslint-disable */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Menu, MenuItem } from '@mui/material';

import './pendingPositionsReport.scss';
import '../WeeklyClosureReport/clouserReport.scss';

import DataGridComponent from '../../../../Components/DataGrid/dataGrid';
import ButtonComponent from '../../../../Components/Button/button';
import DateRangeEditor from '../../../../Components/CustomDateRange/dateRangeInput';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';

import AddEditPendingPositions from './addEditPendingPositionsReport';
import { AuthContext } from '../../../../Contexts/AuthContext';

import { defaultDateRanges } from '../defaultDate';
import { MODULE_NAMES } from '../../../../Constants/modules';
import {
  PENDING_POSITIONS_REPORT,
  DATE_NOTE_HEADING,
  DATE_NOTE_DESCRIPTION,
} from '../../../../Constants/strings';

import ArrowDropDown from '../../../../Assets/images/svg/drop-down-arrow.svg';
import excelIcon from '../../../../Assets/images/svg/excel.svg';
import excelhoverIcon from '../../../../Assets/images/svg/excel_hover.svg';

const PendingPositionsReport = ({
  rows,
  pageSize,
  dateRange,
  setDateRange,
  getPendingPositionsData,
  updatePendingPositionsData,
  downloadPendingPositionsExcel,
}) => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLaptopView, setIsLaptopView] = useState(false);
  const [isExcelHovered, setIsExcelHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingCell, setEditingCell] = useState({ rowId: null, field: null });
  const [editedValues, setEditedValues] = useState({});
  const [errorMessages, setErrorMessages] = useState({});

  const location = useLocation();
  const { snackbarOpen } = useSnackbar();
  const { userProfile } = useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.MIS_STAFF_AUG_REPORT] || [];

  const hasAccess = (action) => permission.includes(action);

  useEffect(() => {
    const initial = defaultDateRanges.find(r => r.label === 'This Week');
    if (initial) handleDateRangeChange(initial);
  }, []);

  useEffect(() => {
    checkResponsiveView();
    window.addEventListener('resize', checkResponsiveView);
    return () => window.removeEventListener('resize', checkResponsiveView);
  }, [location]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      getPendingPositionsData(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange]);

  const checkResponsiveView = () => {
    setIsMobileView(window.innerWidth < 1024);
    setIsLaptopView(window.innerWidth >= 1024 && window.innerWidth <= 1200);
  };

  const handleDateRangeChange = (newDateRange) => {
    const { startDate, endDate } = newDateRange;

    const parsedStartDate = startDate ? new Date(startDate) : null;
    const parsedEndDate = endDate ? new Date(endDate) : null;

    if (parsedStartDate && isNaN(parsedStartDate)) {
      console.error('Invalid startDate in handleDateRangeChange:', startDate);
      return;
    }

    if (parsedEndDate && isNaN(parsedEndDate)) {
      console.error('Invalid endDate in handleDateRangeChange:', endDate);
      return;
    }

    setDateRange({ startDate: parsedStartDate, endDate: parsedEndDate });

    if (parsedStartDate && parsedEndDate) {
      getPendingPositionsData(parsedStartDate, parsedEndDate);
    }
  };

  const handleEditClick = (id, field, value) => {
    if (id === 'Total') return;
    if (!dateRange.startDate || !dateRange.endDate) {
      snackbarOpen(PENDING_POSITIONS_REPORT.INVALID_DATE_RANGE, 'warning');
      return;
    }
    setEditingCell({ rowId: id, field });
    setEditedValues({ [field]: value });
  };

  const handleCancelEdit = () => {
    setEditingCell({ rowId: null, field: null });
    setEditedValues({});
    setErrorMessages({});
  };

  const handleSaveEdit = async (id, field) => {
    try {
      const row = rows.find(r => r.id === id) || {};
      const payload = {
        id: row.summaryId || null,
        verticalId: row.verticalId,
        candidateIdentification:
          field === PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.PENDING_CANDIDATE_IDENTIFICATIONS
            ? editedValues[field]
            : row.pendingCandidateIndentification || '',
        interviewFeedback:
          field === PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.PENDING_INTERVIEW
            ? editedValues[field]
            : row.pendingInterviewFeedback || '',
        startDate: format(new Date(dateRange.startDate), 'yyyy-MM-dd'),
        endDate: format(new Date(dateRange.endDate), 'yyyy-MM-dd'),
        lastUpdatedBy: userProfile?.user_name,
      };

      const result = await updatePendingPositionsData(payload);
      if (result.success) {
        snackbarOpen(result.message, 'success');
        handleCancelEdit();
      } else {
        snackbarOpen(result.message, 'error');
      }
    } catch (error) {
      console.error('Save Error:', error);
      snackbarOpen(PENDING_POSITIONS_REPORT.ERROR_SAVING, 'error');
    }
  };

  const renderEditableCell = (params) => (
    <AddEditPendingPositions
      {...{ params, editingCell, editedValues, setEditedValues, errorMessages, setErrorMessages, handleEditClick, handleSaveEdit, handleCancelEdit, dateRange, snackbarOpen, hasAccess }}
    />
  );

  const baseColumns = useMemo(() => [
    {
      field: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.VERTICAL,
      headerName: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_HEADERS.VERTICAL,
      width: isMobileView ? 165 : 155,
      flex: 0.1,
      minWidth: 165,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: 'vertical-column-cell',
    },
    {
      field: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.DEAL_TYPE,
      headerName: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_HEADERS.DEAL_TYPE,
      width: isMobileView ? 180 : 150,
      flex: 0.15,
      minWidth: 180,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: 'vertical-column-cell',
    },
    {
      field: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.GEOGRAPHY,
      headerName: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_HEADERS.GEOGRAPHY,
      width: isMobileView ? 180 : 150,
      flex: 0.15,
      minWidth: 180,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: 'vertical-column-cell',
    },
    {
      field: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.PENDING_POSITIONS,
      headerName: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_HEADERS.PENDING_POSITIONS,
      width: 212,
      minWidth: 172,
      flex: 0.15,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.PENDING_JD,
      headerName: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_HEADERS.PENDING_JD,
      width: 155,
      minWidth: 125,
      flex: 0.1,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.PENDING_CANDIDATE_IDENTIFICATIONS,
      headerName: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_HEADERS.PENDING_CANDIDATE_IDENTIFICATIONS,
      width: 340,
      minWidth: 280,
      flex: 0.18,
      disableColumnMenu: true,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: renderEditableCell,
    },
    {
      field: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_FIELDS.PENDING_INTERVIEW,
      headerName: PENDING_POSITIONS_REPORT.PENDING_POSITIONS_HEADERS.PENDING_INTERVIEW,
      width: 350,
      minWidth: 320,
      flex: 0.2,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: renderEditableCell,
    },
  ], [isMobileView]);

  const handleDownloadClick = (e) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleDownloadExcel = async () => {
    try {
      const { startDate, endDate } = dateRange;
      await downloadPendingPositionsExcel(startDate, endDate);
    } catch (error) {
      console.error('Download error:', error);
      snackbarOpen(PENDING_POSITIONS_REPORT.DOWNLOAD_FAILURE_MESSAGE, 'error');
    } finally {
      handleCloseMenu();
    }
  };

  const getRowHeight = (params) =>
    editingCell.rowId === params.id && errorMessages[editingCell.field] ? 80 : 52;

  return (
    <div className="pending-position-report-outer-layer">
      <div className="pending-position-report">
        <div className="pending-position-top-section">
          <div className="pending-position-date-filter">
            <DateRangeEditor
              key={`pending-positions-date-range-${dateRange.startDate}-${dateRange.endDate}`}
              dateRange={{
                startDate: dateRange.startDate ? new Date(dateRange.startDate) : null,
                endDate: dateRange.endDate ? new Date(dateRange.endDate) : null,
              }}
              onChange={(newDateRange) => handleDateRangeChange(newDateRange)}
              fromPlaceholder="From Date"
              toPlaceholder="To Date"
              definedRanges={defaultDateRanges}
              dateFormat="dd/mm/yyyy"
              useCurrentYear
              splitInitialCustomMonthView={true}
            />
            <div className="staff-aug-date-range-note">
              <strong className='staff-aug-date-heading'>{DATE_NOTE_HEADING}</strong> {DATE_NOTE_DESCRIPTION}
            </div>
          </div>
          <div>
            <ButtonComponent
              label={PENDING_POSITIONS_REPORT.DOWNLOAD}
              variant="text"
              btnCss="download-skill-btn"
              onClick={handleDownloadClick}
              endIcon={<img src={ArrowDropDown} alt="Arrow Dropdown" />}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
              <MenuItem
                onClick={handleDownloadExcel}
                onMouseEnter={() => setIsExcelHovered(true)}
                onMouseLeave={() => setIsExcelHovered(false)}
                className="downloadskill-dropdown"
              >
                <img className="excel-icon" src={isExcelHovered ? excelhoverIcon : excelIcon} alt="Excel Icon" />
                Download as Excel
              </MenuItem>
            </Menu>
          </div>
        </div>
        <div className="pending-position-report-grid-container">
          <DataGridComponent
            dataRows={rows}
            dataColumns={baseColumns}
            sortFilterColumns={baseColumns.slice(0, 5).map(col => col.field)}
            pageSize={pageSize}
            hideFooter
            isFilteredRows={rows}
            dataGridCss='pending-position-report-grid'
            rowHeightCondition={getRowHeight}
            sortingMode='server'
            paginationMode='server'
          />
        </div>
      </div>
    </div>
  );
};

PendingPositionsReport.propTypes = {
  rows: PropTypes.array.isRequired,
  pageSize: PropTypes.number.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    label: PropTypes.string,
  }).isRequired,
  setDateRange: PropTypes.func.isRequired,
  getPendingPositionsData: PropTypes.func.isRequired,
  updatePendingPositionsData: PropTypes.func.isRequired,
  downloadPendingPositionsExcel: PropTypes.func.isRequired,
};

export default PendingPositionsReport;
